import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import {Breadcrumbs} from '../../components/breadcrumbs'
import {Layout} from '../../components/layout'
import {Box, Container, Text} from '../../vanilla'
import {TObservablePage} from '../../types/commerce'
import {Seo} from '../../components/seo'
import {ChangeAddress} from './partials/change-address'
import {DeliveryBox} from './partials/delivery-box'
import {useBasketStore, useCustomerStore, useDeliveryStore} from '../../store/hooks/useStore'
import LoadingOverlay from '../../components/loading-overlay'
import useNavigation from '../../hooks/use-navigation'
import {useLocation} from 'react-router-dom'
import {usePageDataTracking} from '../../analytics/page-tracking'

const SelectDelivery: TObservablePage = () => {
  const location = useLocation<{originPath?: string}>()
  const originPath = location?.state?.originPath
  const {basket, deliveryPostalCode} = useBasketStore()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()
  const {availableDeliveryTypes, loadAvailability, minDeliveryThreshold} = useDeliveryStore()
  const [isLoading, setIsLoading] = useState(true)
  const sendPageData = usePageDataTracking()
  const pageType = 'Select Delivery Type'
  const pageCategory = 'Delivery'
  const pageSubCategory = 'Delivery'
  const hasAvailableDeliveryMode = availableDeliveryTypes.some(({available}) => available)

  useEffect(() => {
    if (isLoading || !hasAvailableDeliveryMode) return
    sendPageData(pageType, pageCategory, pageSubCategory)
  }, [isLoading, hasAvailableDeliveryMode])

  // Re-direct if customer is not logged in
  const navigate = useNavigation()
  useEffect(() => {
    if (!loadingCustomer && !isRegistered) {
      // Ensure customer returns to this page once logged
      navigate('/login', 'push', {
        directedFrom: '/select-delivery',
        originPath
      })
      return
    }
  }, [loadingCustomer, isRegistered, originPath])

  useEffect(() => {
    if (!basket?.basketId) return

    let isMounted = true

    const loadDeliveryAvailability = async () => {
      try {
        await loadAvailability()
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadDeliveryAvailability()

    return () => {
      isMounted = false
    }
  }, [basket?.basketId, deliveryPostalCode, loadAvailability])

  return (
    <Layout>
      <Seo
        title="Choose Your Delivery | Iceland Delivery"
        description="Choose a delivery slot with Iceland and receive your order at a time that suits you."
      />
      <Container paddingX={'16px'} paddingBottom='16px'>
        <Box paddingY="12px" data-test-selector="select-delivery-breadcrumbs">
          <Breadcrumbs breadcrumbs={[{id: 'SELECT_DELIVERY', name: 'Choose delivery'}]} />
        </Box>

        <Text
          paddingBottom="40px"
          as="h2"
          variant="heading4"
          lineHeight="taller"
          data-test-selector="select-delivery-heading"
        >
          Choose Your Delivery
        </Text>

        <ChangeAddress />

        <Box position="relative" minHeight="180px" data-test-selector="select-delivery-options">
          <LoadingOverlay isLoading={isLoading} text="Loading delivery options..." />
          {!isLoading && (
            <Box
              display="grid"
              gridTemplateColumns={['1x', '4x']}
              gap="16px"
              marginTop="24px"
              data-test-selector="select-delivery-options-grid"
            >
              {availableDeliveryTypes.map((delivery) => (
                <DeliveryBox
                  key={delivery.deliveryType}
                  deliveryType={delivery.deliveryType}
                  available={delivery.available}
                  lowestCost={delivery.lowestCost}
                  unavailableMessage={delivery.unavailableMessage}
                  deliveryTypeSubtext={delivery.deliveryTypeSubtext}
                  features={delivery.features}
                  minSpend={minDeliveryThreshold}
                />
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Layout>
  )
}

export default observer(SelectDelivery)
